import React, { useEffect, useState, useRef } from 'react';
import { app } from '../../firebase/firebaseConfig';
import { collection, doc, getDoc, getFirestore, onSnapshot, setDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import {
  ArrowLeft,
  Gamepad2,
  Monitor,
  Camera,
  Power,
  Circle,
  Square,
  Triangle,
  MousePointer,
  X,
  Eye,
  Lightbulb,
  Activity,
  Bug,
  RefreshCw,
  ClipboardCheck,
  AlertTriangle
} from 'lucide-react';

function RemoteManagementViewer({ initialDevice, onBack, allDevices }) {
  const [selectedDevice, setSelectedDevice] = useState(initialDevice);
  const [remoteManagementData, setRemoteManagementData] = useState({});
  const [screenshot, setScreenshot] = useState(null);
  const [availableDevices, setAvailableDevices] = useState([]);
  
  // Auto-recapture state
  const [autoRecapture, setAutoRecapture] = useState(false);
  const [recaptureInterval, setRecaptureInterval] = useState(5);
  const [captureStatus, setCaptureStatus] = useState(null); // 'requesting' | null
  const [lastCaptureTime, setLastCaptureTime] = useState(null);

  // Confirmation dialog state
  const [showQaRepeatConfirm, setShowQaRepeatConfirm] = useState(false);
  const [showRestartConfirm, setShowRestartConfirm] = useState(false);

  // Test videos for video capture override
  const [testVideos, setTestVideos] = useState([]);

  // Sequence execution state (e.g. 'enterLeft', 'leaveIgloo', etc.)
  const [runningSequence, setRunningSequence] = useState(null);
  
  // Use refs to track listeners and timers without causing re-renders
  const configListenerRef = useRef(null);
  const screenshotListenerRef = useRef(null);
  const autoRecaptureTimeoutRef = useRef(null);
  const autoRecaptureRef = useRef(autoRecapture);
  const recaptureIntervalRef = useRef(recaptureInterval);

  // Keep refs in sync with state
  useEffect(() => {
    autoRecaptureRef.current = autoRecapture;
  }, [autoRecapture]);

  useEffect(() => {
    recaptureIntervalRef.current = recaptureInterval;
  }, [recaptureInterval]);

  // Initialize available devices for dropdown
  useEffect(() => {
    const initializeAvailableDevices = async () => {
      const db = getFirestore(app);
      const devicesWithNames = await Promise.all(
        allDevices.map(async (device) => {
          try {
            const familySnap = await getDoc(doc(db, 'familyInfo', device.id));
            const familyName = familySnap.exists() ? familySnap.data().name : device.id;
            return { ...device, familyName };
          } catch (e) {
            console.error(`Could not fetch family info for ${device.id}`, e);
            return { ...device, familyName: device.id };
          }
        })
      );
      setAvailableDevices(devicesWithNames);
    };

    initializeAvailableDevices();
  }, [allDevices]);

  // Setup Firebase listener for selected device
  useEffect(() => {
    if (!selectedDevice) return;

    // Clean up previous listener
    if (configListenerRef.current) {
      try {
        configListenerRef.current();
        configListenerRef.current = null;
      } catch (error) {
        console.error('Error cleaning up previous config listener:', error);
      }
    }

    try {
      const db = getFirestore(app);
      const remoteManagementRef = doc(db, 'deviceManagement', selectedDevice.id, 'remoteManagement', 'config');
      
      const unsubscribe = onSnapshot(remoteManagementRef, (docSnap) => {
        try {
          if (docSnap.exists()) {
            setRemoteManagementData(docSnap.data());
          } else {
            // Initialize with default values if document doesn't exist
            setRemoteManagementData({
              uiOverride: false,
              systemOverride: false,
              sleep: false,
              remoteView: false
            });
          }
        } catch (error) {
          console.error('Error processing remote management data:', error);
        }
      }, (error) => {
        console.error('Error listening to remote management data:', error);
      });

      // Store the unsubscribe function in ref
      configListenerRef.current = unsubscribe;

    } catch (error) {
      console.error('Error setting up remote management listener:', error);
    }

    // Cleanup on unmount or device change
    return () => {
      if (configListenerRef.current) {
        try {
          configListenerRef.current();
          configListenerRef.current = null;
        } catch (error) {
          console.error('Error cleaning up config listener:', error);
        }
      }
    };
  }, [selectedDevice]);

  // Setup screenshot listener when remoteView is enabled
  useEffect(() => {
    // Clean up previous screenshot listener
    if (screenshotListenerRef.current) {
      try {
        screenshotListenerRef.current();
        screenshotListenerRef.current = null;
      } catch (error) {
        console.error('Error cleaning up previous screenshot listener:', error);
      }
    }

    if (!selectedDevice || !remoteManagementData.remoteView) {
      setScreenshot(null);
      return;
    }

    try {
      const db = getFirestore(app);
      const screenshotRef = doc(db, 'deviceManagement', selectedDevice.id, 'remoteManagement', 'screenshots');
      
      const unsubscribe = onSnapshot(screenshotRef, (docSnap) => {
        try {
          if (docSnap.exists()) {
            const data = docSnap.data();
            let newScreenshot = null;
            
            // Handle new PNG URL format
            if (data.url) {
              newScreenshot = {
                url: data.url,
                timestamp: data.timestamp,
                size: data.size,
                format: data.format || 'png'
              };
            } else if (data.latest) {
              // Fallback to old base64 format
              newScreenshot = data.latest;
            }
            
            if (newScreenshot) {
              setScreenshot(newScreenshot);
              setLastCaptureTime(new Date());
              setCaptureStatus(null); // Clear "requesting" status
              
              // If auto-recapture is enabled, schedule next capture
              if (autoRecaptureRef.current) {
                scheduleNextCapture();
              }
            }
          }
        } catch (error) {
          console.error('Error processing screenshot data:', error);
        }
      }, (error) => {
        console.error('Error listening to screenshot data:', error);
      });

      // Store the unsubscribe function in ref
      screenshotListenerRef.current = unsubscribe;

    } catch (error) {
      console.error('Error setting up screenshot listener:', error);
    }

    // Cleanup listener when effect dependencies change or component unmounts
    return () => {
      if (screenshotListenerRef.current) {
        try {
          screenshotListenerRef.current();
          screenshotListenerRef.current = null;
        } catch (error) {
          console.error('Error cleaning up screenshot listener:', error);
        }
      }
    };
  }, [selectedDevice, remoteManagementData.remoteView]);

  // Clear all signals on component mount and cleanup on unmount
  useEffect(() => {
    // Clear all signals when component mounts
    if (selectedDevice) {
      clearAllSignals();
    }

    return () => {
      // Clean up all listeners on component unmount
      if (configListenerRef.current) {
        try {
          configListenerRef.current();
        } catch (error) {
          console.error('Error cleaning up config listener on unmount:', error);
        }
      }
      if (screenshotListenerRef.current) {
        try {
          screenshotListenerRef.current();
        } catch (error) {
          console.error('Error cleaning up screenshot listener on unmount:', error);
        }
      }
      if (autoRecaptureTimeoutRef.current) {
        clearTimeout(autoRecaptureTimeoutRef.current);
      }
      // Clear state
      setScreenshot(null);
      setRemoteManagementData({});
    };
  }, [selectedDevice]);

  // Fetch test videos when system debug is enabled
  useEffect(() => {
    if (!remoteManagementData.systemDebug) {
      setTestVideos([]);
      return;
    }

    try {
      const db = getFirestore(app);
      const unsubscribe = onSnapshot(collection(db, 'testVideos'), (snapshot) => {
        const videos = snapshot.docs.map(d => ({
          id: d.id,
          ...d.data()
        }));
        videos.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setTestVideos(videos);
      }, (error) => {
        console.error('Error listening to testVideos:', error);
      });

      return () => unsubscribe();
    } catch (error) {
      console.error('Error setting up testVideos listener:', error);
    }
  }, [remoteManagementData.systemDebug]);

  const handleDeviceChange = (deviceId) => {
    const device = availableDevices.find(d => d.id === deviceId);
    if (device) {
      // Stop auto-recapture when switching devices
      if (autoRecapture) {
        stopAutoRecapture();
      }
      
      setSelectedDevice(device);
      setScreenshot(null);
      setRemoteManagementData({});
      setCaptureStatus(null);
      setLastCaptureTime(null);
    }
  };

  const updateRemoteManagementSetting = async (setting, value) => {
    if (!selectedDevice) return;

    try {
      const db = getFirestore(app);
      const remoteManagementRef = doc(db, 'deviceManagement', selectedDevice.id, 'remoteManagement', 'config');
      
      await setDoc(remoteManagementRef, {
        [setting]: value
      }, { merge: true });
      
    } catch (error) {
      console.error(`Error updating ${setting}:`, error);
    }
  };

  const clearAllSignals = async () => {
    if (!selectedDevice) return;

    try {
      const db = getFirestore(app);
      const uiSignalsRef = doc(db, 'deviceManagement', selectedDevice.id, 'remoteManagement', 'uiSignals');
      const systemSignalsRef = doc(db, 'deviceManagement', selectedDevice.id, 'remoteManagement', 'systemSignals');
      const screenshotSignalsRef = doc(db, 'deviceManagement', selectedDevice.id, 'remoteManagement', 'screenshotSignals');
      
      // Delete all signal documents
      await Promise.all([
        uiSignalsRef.delete(),
        systemSignalsRef.delete(),
        screenshotSignalsRef.delete()
      ]);
      
      console.log('All signals cleared for device:', selectedDevice.id);
    } catch (error) {
      console.error('Error clearing signals:', error);
    }
  };

  const triggerUiSignal = async (signalName) => {
    if (!selectedDevice) return;

    try {
      const db = getFirestore(app);
      const uiSignalsRef = doc(db, 'deviceManagement', selectedDevice.id, 'remoteManagement', 'uiSignals');
      
      await setDoc(uiSignalsRef, {
        [signalName]: true
      }, { merge: true });
      
      console.log(`UI signal triggered: ${signalName}`);
    } catch (error) {
      console.error(`Error triggering UI signal ${signalName}:`, error);
    }
  };

  const triggerSystemSignal = async (signalName) => {
    if (!selectedDevice) return;

    try {
      const db = getFirestore(app);
      const systemSignalsRef = doc(db, 'deviceManagement', selectedDevice.id, 'remoteManagement', 'systemSignals');

      await setDoc(systemSignalsRef, {
        [signalName]: true
      }, { merge: true });

      console.log(`System signal triggered: ${signalName}`);
    } catch (error) {
      console.error(`Error triggering system signal ${signalName}:`, error);
    }
  };

  const triggerDebugSignal = async (signalName) => {
    if (!selectedDevice) return;

    try {
      const db = getFirestore(app);
      const debugSignalsRef = doc(db, 'deviceManagement', selectedDevice.id, 'remoteManagement', 'debugSignals');

      await setDoc(debugSignalsRef, {
        [signalName]: true
      }, { merge: true });

      console.log(`Debug signal triggered: ${signalName}`);
    } catch (error) {
      console.error(`Error triggering debug signal ${signalName}:`, error);
    }
  };

  const handleSystemDebugChange = (checked) => {
    updateRemoteManagementSetting('systemDebug', checked);
  };

  const handleQaRepeatConfirm = () => {
    triggerDebugSignal('triggerQaRepeat');
    setShowQaRepeatConfirm(false);
  };

  const handleRestartConfirm = () => {
    triggerDebugSignal('restartApp');
    setShowRestartConfirm(false);
  };

  // Run a sequence of UI signals with delays between each step
  const runUiSignalSequence = async (sequenceName, signals) => {
    setRunningSequence(sequenceName);
    try {
      for (let i = 0; i < signals.length; i++) {
        await triggerUiSignal(signals[i]);
        // Wait 3 seconds between signals (but not after the last one)
        if (i < signals.length - 1) {
          await new Promise(resolve => setTimeout(resolve, 3000));
        }
      }
    } finally {
      setRunningSequence(null);
    }
  };

  // Auto-recapture helper functions
  const scheduleNextCapture = () => {
    // Clear any existing timeout
    if (autoRecaptureTimeoutRef.current) {
      clearTimeout(autoRecaptureTimeoutRef.current);
    }
    
    const interval = recaptureIntervalRef.current * 1000;
    
    // Schedule next capture
    autoRecaptureTimeoutRef.current = setTimeout(() => {
      if (autoRecaptureRef.current && selectedDevice) {
        requestScreenshot();
      }
    }, interval);
  };

  const stopAutoRecapture = () => {
    setAutoRecapture(false);
    if (autoRecaptureTimeoutRef.current) {
      clearTimeout(autoRecaptureTimeoutRef.current);
      autoRecaptureTimeoutRef.current = null;
    }
  };

  const requestScreenshot = async () => {
    if (!selectedDevice) return;

    try {
      setCaptureStatus('requesting');
      const db = getFirestore(app);
      const screenshotSignalsRef = doc(db, 'deviceManagement', selectedDevice.id, 'remoteManagement', 'screenshotSignals');
      
      await setDoc(screenshotSignalsRef, {
        requestScreenshot: true
      }, { merge: true });
      
      console.log('Screenshot requested');
    } catch (error) {
      console.error('Error requesting screenshot:', error);
      setCaptureStatus(null);
    }
  };

  const handleUiOverrideChange = (checked) => {
    updateRemoteManagementSetting('uiOverride', checked);
  };

  const handleSystemOverrideChange = (checked) => {
    updateRemoteManagementSetting('systemOverride', checked);
  };

  const handleSleepChange = (checked) => {
    updateRemoteManagementSetting('sleep', checked);
  };

  const handleAutoEnterChange = (checked) => {
    updateRemoteManagementSetting('autoEnter', checked);
  };

  const handleAutoEnterIglooChange = (value) => {
    updateRemoteManagementSetting('autoEnterIgloo', value);
  };

  const handleRemoteViewChange = (checked) => {
    updateRemoteManagementSetting('remoteView', checked);
    
    // Stop auto-recapture when remote view is disabled
    if (!checked && autoRecapture) {
      stopAutoRecapture();
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  return (
    <div className="mx-auto p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={onBack} className="flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Devices
          </Button>
          <Gamepad2 className="w-8 h-8" />
          <h1 className="text-3xl font-bold">Remote Management</h1>
        </div>

        {/* Device Selector */}
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium">Device:</span>
          <Select
            value={selectedDevice?.id || ''}
            onValueChange={handleDeviceChange}
          >
            <SelectTrigger className="w-64">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {availableDevices.map(device => (
                <SelectItem key={device.id} value={device.id}>
                  <div className="flex items-center gap-2">
                    <Monitor className="w-4 h-4" />
                    {device.familyName}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - UI and System Controls */}
        <div className="space-y-6">
          {/* Section 1: UI Override */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MousePointer className="w-5 h-5" />
                UI Override
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                <Switch
                  checked={remoteManagementData.uiOverride || false}
                  onCheckedChange={handleUiOverrideChange}
                />
                <span className="font-medium">Enable UI Override</span>
              </div>
              
              {remoteManagementData.uiOverride && (
                <div className="space-y-3 pt-4 border-t">
                  {/* UV Update buttons in a row */}
                  <div className="grid grid-cols-3 gap-2">
                    <Button
                      variant="outline"
                      onClick={() => triggerUiSignal('uvUpdateLeft')}
                      className="flex items-center gap-2"
                    >
                      <Triangle className="w-4 h-4 -rotate-90" />
                      Left
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => triggerUiSignal('uvUpdateCentre')}
                      className="flex items-center gap-2"
                    >
                      <Circle className="w-4 h-4" />
                      Centre
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => triggerUiSignal('uvUpdateRight')}
                      className="flex items-center gap-2"
                    >
                      <Triangle className="w-4 h-4 rotate-90" />
                      Right
                    </Button>
                  </div>
                  
                  {/* Full-width buttons below */}
                  <Button
                    variant="outline"
                    onClick={() => triggerUiSignal('deactivate')}
                    className="w-full flex items-center gap-2"
                  >
                    <X className="w-4 h-4" />
                    Deactivate
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => triggerUiSignal('clickUp')}
                    className="w-full flex items-center gap-2"
                  >
                    <MousePointer className="w-4 h-4" />
                    Click Up
                  </Button>

                  {/* Enter/Leave Igloo shortcuts */}
                  <div className="pt-3 border-t">
                    <div className="text-xs text-muted-foreground mb-2">Enter / Exit Igloo Sequency Shortcuts</div>
                    <div className="grid grid-cols-4 gap-1.5">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!!runningSequence}
                        onClick={() => runUiSignalSequence('enterLeft', ['uvUpdateLeft', 'clickUp'])}
                        className="text-xs px-1"
                      >
                        {runningSequence === 'enterLeft' ? '...' : 'Enter Left'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!!runningSequence}
                        onClick={() => runUiSignalSequence('enterCentre', ['uvUpdateCentre', 'clickUp'])}
                        className="text-xs px-1"
                      >
                        {runningSequence === 'enterCentre' ? '...' : 'Enter Centre'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!!runningSequence}
                        onClick={() => runUiSignalSequence('enterRight', ['uvUpdateRight', 'clickUp'])}
                        className="text-xs px-1"
                      >
                        {runningSequence === 'enterRight' ? '...' : 'Enter Right'}
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={!!runningSequence}
                        onClick={() => runUiSignalSequence('leaveIgloo', ['uvUpdateCentre', 'uvUpdateRight', 'clickUp'])}
                        className="text-xs px-1"
                      >
                        {runningSequence === 'leaveIgloo' ? '...' : 'Leave Igloo'}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Section 2: System Override */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Monitor className="w-5 h-5" />
                System Override
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                <Switch
                  checked={remoteManagementData.systemOverride || false}
                  onCheckedChange={handleSystemOverrideChange}
                />
                <span className="font-medium">Enable System Override</span>
              </div>
              
              {remoteManagementData.systemOverride && (
                <div className="space-y-4 pt-4 border-t">
                  {/* HDMI Controls */}
                  <div className="grid grid-cols-2 gap-3">
                    <Button
                      variant="outline"
                      onClick={() => triggerSystemSignal('hdmiOn')}
                      className="flex items-center gap-2"
                    >
                      <Monitor className="w-4 h-4" />
                      HDMI On
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => triggerSystemSignal('hdmiOff')}
                      className="flex items-center gap-2"
                    >
                      <Monitor className="w-4 h-4 opacity-50" />
                      HDMI Off
                    </Button>
                  </div>

                  {/* LED Control */}
                  <div className="pt-3 border-t">
                    <div className="grid grid-cols-3 gap-2">
                      <Button
                        variant="outline"
                        onClick={() => triggerSystemSignal('ledOn')}
                        className="flex items-center gap-2"
                      >
                        <Lightbulb className="w-4 h-4" />
                        LED On
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => triggerSystemSignal('ledOff')}
                        className="flex items-center gap-2"
                      >
                        <Lightbulb className="w-4 h-4 opacity-50" />
                        LED Off
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => triggerSystemSignal('ledBeat')}
                        className="flex items-center gap-2"
                      >
                        <Activity className="w-4 h-4" />
                        LED Beat
                      </Button>
                    </div>
                  </div>

                  {/* Sleep Control */}
                  <div className="flex items-center gap-2 pt-2 border-t">
                    <Switch
                      checked={remoteManagementData.sleep || false}
                      onCheckedChange={handleSleepChange}
                    />
                    <span className="font-medium">Sleep Mode</span>
                  </div>

                  {/* Auto-Enter Control */}
                  <div className="pt-3 border-t space-y-3">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={remoteManagementData.autoEnter || false}
                        onCheckedChange={handleAutoEnterChange}
                      />
                      <span className="font-medium">Auto-Enter</span>
                    </div>

                    {remoteManagementData.autoEnter && (
                      <div className="space-y-3 pl-6">
                        {/* Igloo Selection */}
                        <div className="space-y-1">
                          <label className="text-sm text-muted-foreground">Target Igloo</label>
                          <select
                            value={remoteManagementData.autoEnterIgloo || 'centre'}
                            onChange={(e) => handleAutoEnterIglooChange(e.target.value)}
                            className="w-full p-2 border rounded-md bg-background"
                          >
                            <option value="left">Left</option>
                            <option value="centre">Centre</option>
                            <option value="right">Right</option>
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Section 3: System Debug */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bug className="w-5 h-5" />
                System Debug
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                <Switch
                  checked={remoteManagementData.systemDebug || false}
                  onCheckedChange={handleSystemDebugChange}
                />
                <span className="font-medium">Enable System Debug</span>
              </div>

              {remoteManagementData.systemDebug && (
                <div className="space-y-3 pt-4 border-t">
                  {/* QA Repeat Button */}
                  <Button
                    variant="outline"
                    onClick={() => setShowQaRepeatConfirm(true)}
                    className="w-full flex items-center gap-2"
                  >
                    <ClipboardCheck className="w-4 h-4" />
                    Trigger QA Repeat
                  </Button>

                  {/* Restart App Button */}
                  <Button
                    variant="outline"
                    onClick={() => setShowRestartConfirm(true)}
                    className="w-full flex items-center gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Restart App
                  </Button>

                  {/* Toggle Camera View Button */}
                  <Button
                    variant="outline"
                    onClick={() => triggerDebugSignal('cameraViewToggle')}
                    className="w-full flex items-center gap-2"
                  >
                    <Camera className="w-4 h-4" />
                    Toggle Camera View
                  </Button>

                  {/* CEC Controls */}
                  <div className="pt-3 border-t space-y-3">
                    <div className="text-sm font-medium">CEC Controls</div>
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        variant="outline"
                        onClick={() => triggerDebugSignal('cecTvOn')}
                        className="flex items-center gap-2"
                      >
                        <Monitor className="w-4 h-4" />
                        TV On
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => triggerDebugSignal('cecTvOff')}
                        className="flex items-center gap-2"
                      >
                        <Monitor className="w-4 h-4" />
                        TV Off
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => triggerDebugSignal('cecSwitchInput')}
                        className="flex items-center gap-2"
                      >
                        <Monitor className="w-4 h-4" />
                        Switch Input
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => triggerDebugSignal('cecQueryPower')}
                        className="flex items-center gap-2"
                      >
                        <Power className="w-4 h-4" />
                        Query Power
                      </Button>
                    </div>
                  </div>

                  {/* QA Repeat Confirmation Dialog */}
                  {showQaRepeatConfirm && (
                    <div className="p-4 border border-yellow-300 rounded-lg bg-yellow-50 space-y-3">
                      <div className="flex items-center gap-2 text-yellow-800">
                        <AlertTriangle className="w-5 h-5" />
                        <span className="font-medium">Confirm QA Repeat</span>
                      </div>
                      <p className="text-sm text-yellow-700">
                        This will set the device to enter QA mode on next restart.
                        The device will keep its existing serial numbers and re-run all QA tests.
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="default"
                          size="sm"
                          onClick={handleQaRepeatConfirm}
                        >
                          Confirm
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowQaRepeatConfirm(false)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Restart Confirmation Dialog */}
                  {showRestartConfirm && (
                    <div className="p-4 border border-yellow-300 rounded-lg bg-yellow-50 space-y-3">
                      <div className="flex items-center gap-2 text-yellow-800">
                        <AlertTriangle className="w-5 h-5" />
                        <span className="font-medium">Confirm App Restart</span>
                      </div>
                      <p className="text-sm text-yellow-700">
                        This will restart the Higlo app on the device. The device will briefly disconnect.
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="default"
                          size="sm"
                          onClick={handleRestartConfirm}
                        >
                          Confirm
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setShowRestartConfirm(false)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Override Video Capture */}
                  <div className="pt-3 border-t space-y-3">
                    <div className="text-sm font-medium">Override Video Capture</div>

                    {/* Video Selection */}
                    <div className="space-y-1">
                      <label className="text-xs text-muted-foreground">Test Video</label>
                      <select
                        value={remoteManagementData.overrideVideoId || ''}
                        onChange={(e) => updateRemoteManagementSetting('overrideVideoId', e.target.value)}
                        className="w-full p-2 border rounded-md bg-background text-sm"
                      >
                        <option value="">Select a video...</option>
                        {testVideos.map(video => (
                          <option key={video.id} value={video.id}>
                            {video.name} ({(video.fileSizeBytes / 1024).toFixed(0)}KB)
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Loop Mode */}
                    <div className="space-y-1">
                      <label className="text-xs text-muted-foreground">Loop Mode</label>
                      <select
                        value={remoteManagementData.overrideLoopMode || 'bounce'}
                        onChange={(e) => updateRemoteManagementSetting('overrideLoopMode', e.target.value)}
                        className="w-full p-2 border rounded-md bg-background text-sm"
                      >
                        <option value="loop">Loop (restart from beginning)</option>
                        <option value="bounce">Bounce (play forward then backward)</option>
                      </select>
                    </div>

                    {/* Enable Toggle */}
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={remoteManagementData.overrideVideoCapture || false}
                        onCheckedChange={(checked) => updateRemoteManagementSetting('overrideVideoCapture', checked)}
                        disabled={!remoteManagementData.overrideVideoId}
                      />
                      <span className="font-medium text-sm">Enable Override</span>
                    </div>

                    {/* Status Display */}
                    {remoteManagementData.overrideVideoStatus && (
                      <div className={`text-xs px-2 py-1 rounded ${
                        remoteManagementData.overrideVideoStatus === 'playing' ? 'bg-green-100 text-green-800' :
                        remoteManagementData.overrideVideoStatus === 'downloading' ? 'bg-yellow-100 text-yellow-800' :
                        remoteManagementData.overrideVideoStatus === 'error' ? 'bg-red-100 text-red-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        Status: {remoteManagementData.overrideVideoStatus}
                        {remoteManagementData.overrideVideoError && ` — ${remoteManagementData.overrideVideoError}`}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Remote View */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Camera className="w-5 h-5" />
                Remote View
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                <Switch
                  checked={remoteManagementData.remoteView || false}
                  onCheckedChange={handleRemoteViewChange}
                />
                <span className="font-medium">Enable Remote View</span>
              </div>
              
              {remoteManagementData.remoteView && (
                <div className="pt-4 border-t space-y-4">
                  {/* Screenshot Request Button */}
                  <Button
                    onClick={requestScreenshot}
                    className="w-full flex items-center gap-2"
                    variant="default"
                    disabled={captureStatus === 'requesting'}
                  >
                    <Camera className="w-4 h-4" />
                    {captureStatus === 'requesting' ? 'Capture Requested...' : 'Capture Screenshot'}
                  </Button>
                  
                  {/* Auto-recapture Controls */}
                  <div className="space-y-3 p-3 bg-muted/30 rounded-lg">
                    <div className="flex items-center gap-2 text-sm">
                      <span>Every</span>
                      <select
                        value={recaptureInterval}
                        onChange={(e) => setRecaptureInterval(Number(e.target.value))}
                        disabled={!autoRecapture}
                        className="px-2 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <option value={3}>3 seconds</option>
                        <option value={5}>5 seconds</option>
                        <option value={10}>10 seconds</option>
                        <option value={15}>15 seconds</option>
                        <option value={30}>30 seconds</option>
                      </select>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={autoRecapture}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setAutoRecapture(true);
                            // Start with an immediate capture
                            requestScreenshot();
                          } else {
                            stopAutoRecapture();
                          }
                        }}
                      />
                      <span className="font-medium">Auto-recapture</span>
                    </div>
                  </div>
                  
                  {lastCaptureTime && (
                    <div className="text-xs text-muted-foreground">
                      Last capture: {lastCaptureTime.toLocaleTimeString()}
                    </div>
                  )}
                  
                  {/* Screenshot Display */}
                  {screenshot ? (
                    <div className="space-y-2">
                      {screenshot.error ? (
                        <div className="p-4 border border-red-200 rounded-lg bg-red-50">
                          <div className="text-red-800 font-medium">Screenshot Error</div>
                          <div className="text-red-600 text-sm">{screenshot.error}</div>
                        </div>
                      ) : (
                        <>
                          <div className="border rounded-lg overflow-hidden bg-muted/50">
                            <img
                              src={screenshot.url ? screenshot.url : `data:image/jpeg;base64,${screenshot.image}`}
                              alt="Device Screenshot"
                              className="w-full h-auto"
                              style={{ maxHeight: '400px', objectFit: 'contain' }}
                            />
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {screenshot.url ? (
                              <>Size: {(screenshot.size / 1024).toFixed(1)}KB | Format: {screenshot.format}</>
                            ) : (
                              <>Resolution: {screenshot.resolution || '640x360'} | Status: {screenshot.status || 'success'}</>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-64 border-2 border-dashed border-muted-foreground/25 rounded-lg">
                      <div className="text-center text-muted-foreground">
                        <Eye className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        <p>No screenshot captured yet</p>
                        <p className="text-xs">Click "Capture Screenshot" to take a snapshot</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default RemoteManagementViewer;
